import { useState, type FormEvent } from 'react'
import { BoutonGoogle, connexionGoogle } from '../api/google'
import { MESSAGE_LIEN_ENVOYE, demanderReinitialisation } from '../api/motDePasse'
import {
  messageCompteCree,
  premiereErreur,
  validerInscription,
  type ChampInscription,
} from '../api/compte'
import { BasculeAcces, idOngletAcces, idPanneauAcces, useContenuAcces, type ModeAcces } from './BasculeAcces'
import { BasculeMethode, ConnexionWhatsapp, type MethodeConnexion } from '../../../../../web-commun/connexion-whatsapp'
import { supabase } from '../api/supabase'
import { lireMethode, memoriserMethode } from '../api/methodeConnexion'

const NOTE_WHATSAPP =
  'Utilisez le numéro WhatsApp que votre administrateur SwiftEats a enregistré pour votre compte. Les comptes sont créés par l’équipe : demandez-lui de vous en créer un.'

const NOTE_COMPTE =
  'Créer un compte ne donne pas accès à la console : un administrateur SwiftEats doit ensuite vous ajouter à l’équipe.'

/**
 * ⚠️ Les classes CSS suivent celles de la console DISPATCH, pas celles du
 * Marchand.
 *
 * Les deux consoles ont des feuilles distinctes : `login-card h1` et
 * `btn btn-accent` ici, `brand` et `btn-main` là-bas. Réutiliser le composant
 * du Marchand tel quel donnait une carte de connexion sans style — le genre de
 * régression qu'aucun compilateur n'attrape et qu'il faut regarder pour voir.
 */
const Cloche = () => (
  <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" width="56" height="56">
    <rect width="40" height="40" rx="12" fill="var(--encre)" />
    <g className="bell-g">
      <circle cx="20" cy="12.4" r="2.1" fill="var(--or)" />
      <path d="M9.4 26.8a10.6 10.6 0 0121.2 0z" fill="var(--or)" />
      <path d="M7 30.2h26" stroke="var(--or)" strokeWidth="2.4" strokeLinecap="round" />
      <path
        d="M14.2 21.8c1.1-2.7 3.2-4.7 5.8-5.4"
        stroke="var(--surface)"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity=".55"
      />
    </g>
  </svg>
)

export function Connexion({
  erreurInitiale,
  surConnexion,
  surInscription,
  surConnexionWhatsapp,
}: {
  erreurInitiale: string | null
  surConnexion: (email: string, motDePasse: string) => Promise<void>
  /** Crée le compte ; `sessionOuverte` faux = confirmation par e-mail requise. */
  surInscription: (nom: string, email: string, motDePasse: string) => Promise<{ sessionOuverte: boolean }>
  /**
   * Appelée une fois le code WhatsApp vérifié (session ouverte) : reprend le MÊME chemin qu'une connexion par mot
   * de passe réussie (contrôle d'accès au personnel, droits, commandes). Renvoie le motif d'un refus, ou `null`.
   */
  surConnexionWhatsapp: () => Promise<string | null>
}) {
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [nom, setNom] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mode, setMode] = useState<ModeAcces>('connexion')
  const { contenu, sortant } = useContenuAcces(mode)
  /* E-mail du compte tout juste créé (panneau de succès). */
  const [compteCree, setCompteCree] = useState<string | null>(null)
  const [champInvalide, setChampInvalide] = useState<ChampInscription | null>(null)
  const [erreur, setErreur] = useState<string | null>(erreurInitiale)
  const [envoi, setEnvoi] = useState(false)
  /* « Mot de passe oublié » : même carte, autre formulaire. */
  const [oubli, setOubli] = useState(false)
  const [lienEnvoye, setLienEnvoye] = useState(false)
  /* Dernier choix mémorisé sur l'appareil ; défaut : WhatsApp. */
  const [methode, setMethode] = useState<MethodeConnexion>(() => lireMethode())
  /* Nouvelle clé = le formulaire WhatsApp repart à l'étape « numéro » (le code vient d'être consommé). */
  const [cleWhatsapp, setCleWhatsapp] = useState(0)

  function changerMethode(m: MethodeConnexion) {
    if (m === methode) return
    setMethode(m)
    memoriserMethode(m)
    setErreur(null)
    setChampInvalide(null)
  }

  async function apresWhatsapp() {
    const refus = await surConnexionWhatsapp()
    if (refus) {
      setErreur(refus)
      setCleWhatsapp((k) => k + 1)
    }
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    setEnvoi(true)
    setErreur(null)
    try {
      await surConnexion(email.trim(), motDePasse)
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Connexion impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  function changerMode(m: ModeAcces) {
    if (m === mode) return
    setMode(m)
    setErreur(null)
    setChampInvalide(null)
    setCompteCree(null)
  }

  async function creer(e: FormEvent) {
    e.preventDefault()
    if (envoi) return
    const premiere = premiereErreur(validerInscription({ nom, email, motDePasse, confirmation }))
    if (premiere) {
      setErreur(premiere.message)
      setChampInvalide(premiere.champ)
      document.getElementById(`ins-${premiere.champ}`)?.focus()
      return
    }
    setEnvoi(true)
    setErreur(null)
    setChampInvalide(null)
    try {
      const r = await surInscription(nom.trim(), email.trim(), motDePasse)
      if (!r.sessionOuverte) {
        setCompteCree(email.trim())
        setMotDePasse('')
        setConfirmation('')
      }
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Création du compte impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  async function demander(e: FormEvent) {
    e.preventDefault()
    setEnvoi(true)
    setErreur(null)
    try {
      await demanderReinitialisation(email)
      setLienEnvoye(true)
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Envoi impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  const lienTexte = {
    background: 'none', border: 'none', padding: 0, margin: '10px auto 0', display: 'block',
    color: 'var(--n700)', fontSize: 12.5, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit',
  } as const

  if (oubli) {
    return (
      <div className="login-wrap" style={{ display: 'flex' }}>
        <form className="login-card" onSubmit={demander}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Cloche />
          </div>
          <h1>
            Swift<span>Eats</span>
          </h1>
          <p className="sub">Mot de passe oublié</p>
          {lienEnvoye ? (
            <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--n700)' }}>{MESSAGE_LIEN_ENVOYE}</p>
          ) : (
            <>
              <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--n700)', margin: '0 0 10px' }}>
                Saisissez l’e-mail de votre compte : nous vous envoyons un lien pour choisir un nouveau
                mot de passe.
              </p>
              <input
                type="email"
                placeholder="E-mail"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <button className="btn btn-accent" type="submit" disabled={envoi} style={{ width: '100%' }}>
                {envoi ? 'Envoi…' : 'Envoyer le lien'}
              </button>
            </>
          )}
          {erreur && (
            <div id="login-err" style={{ marginTop: 8 }}>
              {erreur}
            </div>
          )}
          <button
            type="button"
            style={lienTexte}
            onClick={() => {
              setOubli(false)
              setLienEnvoye(false)
              setErreur(null)
            }}
          >
            Retour à la connexion
          </button>
        </form>
      </div>
    )
  }

  const invalide = (c: ChampInscription) => (champInvalide === c ? true : undefined)

  return (
    <div className="login-wrap" style={{ display: 'flex' }}>
      <div className="login-card">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Cloche />
        </div>
        <h1>
          Swift<span>Eats</span>
        </h1>
        <p className="sub">Dispatch &amp; Operations</p>
        <BasculeMethode methode={methode} surChangement={changerMethode} desactive={envoi} />
        {methode === 'whatsapp' ? (
          <div className="cw-hote bascule-contenu">
            <ConnexionWhatsapp key={cleWhatsapp} client={supabase} surConnecte={apresWhatsapp} />
            <p className="note-acces">{NOTE_WHATSAPP}</p>
            {erreur && (
              <div id="login-err" role="alert" style={{ marginTop: 0 }}>
                {erreur}
              </div>
            )}
          </div>
        ) : (
          <form
            onSubmit={contenu === 'connexion' ? soumettre : creer}
            noValidate={contenu === 'inscription'}
          >
            <BasculeAcces mode={mode} surChangement={changerMode} desactive={envoi} />
            <div
              className={`bascule-contenu${sortant ? ' sortant' : ''}`}
              key={contenu}
              role="tabpanel"
              id={idPanneauAcces}
              aria-labelledby={idOngletAcces(contenu)}
            >
              {contenu === 'connexion' && (
                <>
                  <input
                    type="email"
                    placeholder="E-mail"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Mot de passe"
                    autoComplete="current-password"
                    value={motDePasse}
                    onChange={(e) => setMotDePasse(e.target.value)}
                    required
                  />
                  {/* `type="submit"` : la touche Entrée doit valider. Sur la console
                      actuelle le bouton porte un `onclick`, donc Entrée ne fait rien. */}
                  <button className="btn btn-accent" type="submit" disabled={envoi} style={{ width: '100%' }}>
                    {envoi ? 'Connexion…' : 'Se connecter'}
                  </button>
                  <button
                    type="button"
                    style={lienTexte}
                    onClick={() => {
                      setOubli(true)
                      setErreur(null)
                    }}
                  >
                    Mot de passe oublié ?
                  </button>
                </>
              )}
              {contenu === 'inscription' && compteCree === null && (
                <>
                  <input
                    id="ins-nom"
                    type="text"
                    placeholder="Nom complet"
                    autoComplete="name"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    aria-invalid={invalide('nom')}
                    required
                  />
                  <input
                    id="ins-email"
                    type="email"
                    placeholder="E-mail"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={invalide('email')}
                    required
                  />
                  <input
                    id="ins-motDePasse"
                    type="password"
                    placeholder="Mot de passe (8 caractères minimum)"
                    autoComplete="new-password"
                    minLength={8}
                    value={motDePasse}
                    onChange={(e) => setMotDePasse(e.target.value)}
                    aria-invalid={invalide('motDePasse')}
                    required
                  />
                  <input
                    id="ins-confirmation"
                    type="password"
                    placeholder="Confirmer le mot de passe"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    aria-invalid={invalide('confirmation')}
                    required
                  />
                  <p className="note-acces">{NOTE_COMPTE}</p>
                  <button className="btn btn-accent" type="submit" disabled={envoi} style={{ width: '100%' }}>
                    {envoi ? 'Création…' : 'Créer mon compte'}
                  </button>
                </>
              )}
              {contenu === 'inscription' && compteCree !== null && (
                <>
                  <p className="compte-cree" role="status">
                    {messageCompteCree(compteCree)}
                  </p>
                  <p className="note-acces">{NOTE_COMPTE}</p>
                  <button
                    className="btn btn-accent"
                    type="button"
                    style={{ width: '100%' }}
                    onClick={() => changerMode('connexion')}
                  >
                    Aller à la connexion
                  </button>
                </>
              )}
              {contenu === 'connexion' && (
                <BoutonGoogle connexion={connexionGoogle} surErreur={(m) => setErreur(m || null)} />
              )}
              {erreur && (
                <div id="login-err" role="alert" style={{ marginTop: 8 }}>
                  {erreur}
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
